import React, {useContext} from 'react'
import {Button, FormControl, FormHelperText, Grid, RadioGroup, Stack, Typography,} from '@mui/material'
import {AppContext} from '../../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../../StepperButtons'
import {ClinicalConditionState, ProductTypeOptions,} from '../../../contexts/types'
import PermanentOptions from './PermanentOptions'
import TemporaryOptions from './TemporaryOptions'
import {Trans} from 'react-i18next'
import {useForm} from '../../../hooks/useForm'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";
import {setClinicalCondition} from "../../../contexts/ClinicalConditionReducer";
import useTheHackiestPageNavigation from "../../../hooks/useTheHackiestPageNavigation";

const ClinicalCondition: React.FC = () => {
    const { navigateForward } = useTheHackiestPageNavigation()
    const { complaint, dispatch, postNotification, clearNotification } = useContext(AppContext)
    const { t, tns } = useContext(OrgCustomizationContext)

    const productType = complaint.generalComplaint.productType as ProductTypeOptions
    const required = { value: true, message: t('this_field_is_required') }
    const {
        data,
        errors,
        handleChange,
        validateFields,
        enableOnChangeValidation,
    } = useForm<ClinicalConditionState>({
        initialValues: {
            ...complaint.clinicalCondition,
        },
        validations: {
            clinicalCondition: { required },
            hasPainDisappeared: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'TRANSIENT_PAIN' &&
                            data.hasPainDisappeared === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            hasNumbnessDisappeared: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'NUMBNESS' &&
                            data.hasNumbnessDisappeared === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            wereNerveSymptomsAlleviated: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition ===
                                'NERVE_INJURY_SYMPTOMS' &&
                            data.wereNerveSymptomsAlleviated === ''
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            permanentImpairmentExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'PERMANENT_IMPAIRMENT' &&
                            !data.permanentImpairmentExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            otherExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'OTHER' &&
                            !data.otherExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            prolongedSurgeryExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition === 'PROLONGED_SURGERY' &&
                            !data.prolongedSurgeryExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            prolongedTreatmentExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.clinicalCondition ===
                                'PROLONGED_TREATMENT_TIME' &&
                            !data.prolongedTreatmentExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
        },
    })

    const navigateToNextScreen = () => navigateForward()

    const handleSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange('clinicalCondition', e.target.value)
    }

    const handleSubmit = () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const clinicalCondition: ClinicalConditionState = {
            ...data,
            permanentImpairmentExplanation:
                data.clinicalCondition === 'PERMANENT_IMPAIRMENT'
                    ? data.permanentImpairmentExplanation
                    : '',
            otherExplanation:
                data.clinicalCondition === 'OTHER' ? data.otherExplanation : '',
            prolongedSurgeryExplanation:
                data.clinicalCondition === 'PROLONGED_SURGERY'
                    ? data.prolongedSurgeryExplanation
                    : '',
            prolongedTreatmentExplanation:
                data.clinicalCondition === 'PROLONGED_TREATMENT_TIME'
                    ? data.prolongedTreatmentExplanation
                    : '',
            hasPainDisappeared:
                data.clinicalCondition === 'TRANSIENT_PAIN'
                    ? data.hasPainDisappeared
                    : '',
            hasNumbnessDisappeared:
                data.clinicalCondition === 'NUMBNESS'
                    ? data.hasNumbnessDisappeared
                    : '',
            wereNerveSymptomsAlleviated:
                data.clinicalCondition === 'NERVE_INJURY_SYMPTOMS'
                    ? data.wereNerveSymptomsAlleviated
                    : '',
        }
        
        dispatch(setClinicalCondition(clinicalCondition))

        navigateToNextScreen()
    }

    return (
        <Grid container>
            <Stack spacing={2}>
                <Typography variant='h5' sx={{ mb: 4 }}>
                    {t('cci_clinical_conditions')} -{' '}
                    {t(`pt_option_${productType.toLowerCase()}`).toLowerCase()}
                </Typography>

                <Typography variant='h6' color='secondary.contrastText'>
                    <Trans i18nKey={tns('cci_select_clinical_condition')}>
                        -
                        <Typography
                            component='span'
                            sx={{ color: 'primary.main', fontSize: 'inherit' }}
                        >
                            -
                        </Typography>
                        -
                    </Trans>
                    :
                </Typography>

                <FormControl sx={{ pt: 2 }}>
                    <RadioGroup
                        value={data.clinicalCondition}
                        onChange={handleSelection}
                    >
                        <TemporaryOptions
                            data={data}
                            errors={errors}
                            complaint={complaint}
                            handleChange={handleChange}
                        />

                        <PermanentOptions
                            data={data}
                            errors={errors}
                            complaint={complaint}
                            handleChange={handleChange}
                        />
                    </RadioGroup>
                </FormControl>
                <FormHelperText
                    sx={{ pl: 2 }}
                    error={!!errors.clinicalCondition}
                >
                    {errors.clinicalCondition}
                </FormHelperText>
            </Stack>

            <Stack direction='row-reverse' spacing={2} sx={{ pt: 4, width: '100%' }}>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>
                
                <PreviousButton before={() => dispatch(setClinicalCondition(data))} />
                
                <CancelButton />
            </Stack>
        </Grid>
    )
}

export default ClinicalCondition
